// Regions, Riot ID parsing and "recent searches" for the search bar.

export interface Region {
	slug: string;
	label: string;
}

export const REGIONS: Region[] = [
	{ slug: "euw", label: "EUW" },
	{ slug: "eune", label: "EUNE" },
	{ slug: "na", label: "NA" },
	{ slug: "kr", label: "KR" },
	{ slug: "br", label: "BR" },
	{ slug: "jp", label: "JP" },
	{ slug: "lan", label: "LAN" },
	{ slug: "las", label: "LAS" },
	{ slug: "oce", label: "OCE" },
	{ slug: "tr", label: "TR" },
	{ slug: "ru", label: "RU" },
	{ slug: "me", label: "ME" },
	{ slug: "sg", label: "SG" },
	{ slug: "tw", label: "TW" },
	{ slug: "vn", label: "VN" }
];

export const DEFAULT_REGION = "euw";

/** "Name#TAG" (or "Name-TAG") -> parts, or null when it is not a Riot ID yet. Splits on the last # / dash. */
export function parseRiotId(input: string): { name: string; tag: string } | null {
	const s = input.trim();
	const i = Math.max(s.lastIndexOf("#"), s.lastIndexOf("-"));
	if (i <= 0) return null;
	const name = s.slice(0, i).trim();
	const tag = s.slice(i + 1).trim();
	return name && /^[A-Za-z0-9]{2,6}$/.test(tag) ? { name, tag } : null;
}

/**
 * Riot IDs from a pasted roster: one per line / comma-separated, or an op.gg multi-search link
 * (…/multisearch/euw?summoners=Name%23TAG,Other%23TAG) as found on a Prime League team page. De-duplicated, in order.
 */
export function parseRoster(input: string): { name: string; tag: string }[] {
	let text = input;
	const param = /[?&]summoners=([^&\s]+)/.exec(input)?.[1];
	if (param) {
		try {
			text = decodeURIComponent(param.replace(/\+/g, " "));
		} catch {
			text = param;
		}
	}
	const seen = new Map<string, { name: string; tag: string }>();
	for (const part of text.split(/[,\n]/)) {
		const id = part.includes("#") ? parseRiotId(part) : null; // "#" required: a dash alone is too ambiguous in free text
		const key = id && `${id.name}#${id.tag}`.toLowerCase();
		if (id && key && !seen.has(key)) seen.set(key, id);
	}
	return [...seen.values()];
}

/** Path of a summoner page, e.g. /summoners/euw/Faker-KR1 */
export function summonerPath(region: string, name: string, tag: string): string {
	return `/summoners/${region}/${encodeURIComponent(name)}-${encodeURIComponent(tag)}`;
}

// ---- persisted preferences (localStorage may be unavailable: everything degrades to defaults) ----

const REGION_KEY = "riftwatch.region";
const RECENT_KEY = "riftwatch.recent";
const MAX_RECENT = 8;

export interface RecentSearch {
	name: string;
	tag: string;
	region: string;
	profileIconId?: number;
}

export function getSavedRegion(): string {
	try {
		const r = localStorage.getItem(REGION_KEY);
		return r && REGIONS.some(x => x.slug === r) ? r : DEFAULT_REGION;
	} catch {
		return DEFAULT_REGION;
	}
}

export function saveRegion(slug: string): void {
	try {
		localStorage.setItem(REGION_KEY, slug);
	} catch {
		/* not persisted */
	}
}

export function getRecentSearches(): RecentSearch[] {
	try {
		const list = JSON.parse(localStorage.getItem(RECENT_KEY) ?? "[]");
		return Array.isArray(list) ? list.filter(r => r?.name && r?.tag && r?.region).slice(0, MAX_RECENT) : [];
	} catch {
		return [];
	}
}

/** Most recent first, de-duplicated by Riot ID + region. Keeps a known profile icon when a later entry has none. */
export function rememberSearch(entry: RecentSearch): void {
	try {
		const key = (r: RecentSearch) => `${r.region}:${r.name}#${r.tag}`.toLowerCase();
		const list = getRecentSearches();
		const prev = list.find(r => key(r) === key(entry));
		const rest = list.filter(r => key(r) !== key(entry));
		const merged = { ...entry, profileIconId: entry.profileIconId ?? prev?.profileIconId };
		localStorage.setItem(RECENT_KEY, JSON.stringify([merged, ...rest].slice(0, MAX_RECENT)));
	} catch {
		/* not persisted */
	}
}

export function clearRecentSearches(): void {
	try {
		localStorage.removeItem(RECENT_KEY);
	} catch {
		/* nothing to clear */
	}
}
